import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'store';

import { Change } from '~weekly-planner/reducers/unsaved';

const AppointmentsState = (state: RootState) => state.weeklyPlanner.appointments;
const UnsavedChangesState = (state: RootState) => state.weeklyPlanner.unsaved;

export const selectAll = (state: RootState) => AppointmentsState(state).rows;
export const selectAssignableUsers = (state: RootState) => AppointmentsState(state).assignableUsers;
export const selectExceptions = (state: RootState) => AppointmentsState(state).exceptions;
export const selectOnHold = (state: RootState) => AppointmentsState(state).onHold;
export const selectChanges = (state: RootState) => AppointmentsState(state).changes;

const selectUnsavedChanges = (state: RootState) => UnsavedChangesState(state).appointments;

// Fields that constitute meaningful appointment changes for tracking purposes
const TRACKED_FIELDS: (keyof Change)[] = ['user_id', 'start_time', 'end_time', 'date'];

// Basic import check for repeats & singles - Needs expanding.
const checkUserImport = (original: any, change: any) => {
  if (original.user_id === change.user_id) {
    if (original.id === null && change.parent_repeat_id === original.parent_repeat_id) {
      return true;
    } else if (original.id !== null) {
      return true;
    }
  }
  return false;
};

export const selectChangedAppointments = createSelector([selectUnsavedChanges], (unsavedChanges) => {
  const changes = [];

  for (const [key, { original, change }] of Object.entries(unsavedChanges)) {
    if (!original || !change) continue;

    let hasChange = false;

    const trackedChange = TRACKED_FIELDS.reduce((acc, field) => {
      if (Object.prototype.hasOwnProperty.call(change, field)) {
        // Special logic for user_id field
        if (field === 'user_id') {
          if ((change.user_id ?? null) !== (original.user_id ?? null)) {
            acc[field] = change[field];
            hasChange = true;
          }
          // New appointment assigned to the same user as original unallocated appt (Import case)
          else if (checkUserImport(original, change)) {
            acc[field] = change[field];
            hasChange = true;
          }
        } else {
          if (change[field] !== original[field]) {
            acc[field] = change[field];
            hasChange = true;
          }
        }
      }
      return acc;
    }, {} as Partial<Change>);

    if (hasChange) {
      changes.push({
        id: Number(key),
        original,
        change: trackedChange,
      });
    }
  }

  return changes;
});

export const selectChangeById = createSelector(
  [selectChangedAppointments, (_: RootState, key: number | null) => key],
  (changedAppointments, key) => {
    if (key == null) return false;

    return changedAppointments.some((change) => change.id === key);
  },
);
